import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Plus, Pencil, Trash2 } from "lucide-react";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Checkbox } from "@/components/ui/checkbox";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import api, { formatApiError } from "@/api";

const EMPTY_FORM = { first_name: "", last_name: "", mission: "", email: "", phone: "", consent_rgpd: false, active: true };

export default function AdminVolunteers() {
  const [volunteers, setVolunteers] = useState([]);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(EMPTY_FORM);
  const [saving, setSaving] = useState(false);

  const load = () => api.get("/admin/volunteers").then((r) => setVolunteers(r.data)).catch((e) => toast.error(formatApiError(e)));
  useEffect(() => { load(); }, []);

  const openCreate = () => { setEditing(null); setForm(EMPTY_FORM); setDialogOpen(true); };
  const openEdit = (v) => { setEditing(v); setForm({ ...EMPTY_FORM, ...v }); setDialogOpen(true); };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      if (editing) {
        await api.patch(`/admin/volunteers/${editing.id}`, form);
        toast.success("Bénévole mis à jour");
      } else {
        await api.post("/admin/volunteers", form);
        toast.success("Bénévole ajouté");
      }
      setDialogOpen(false);
      load();
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setSaving(false);
    }
  };

  const remove = async (v) => {
    if (!window.confirm(`Supprimer ${v.first_name} ${v.last_name} de l'annuaire ?`)) return;
    try {
      await api.delete(`/admin/volunteers/${v.id}`);
      toast.success("Bénévole supprimé");
      load();
    } catch (err) {
      toast.error(formatApiError(err));
    }
  };

  return (
    <div className="space-y-6" data-testid="admin-volunteers-page">
      <div className="flex flex-wrap items-center justify-between gap-4">
        <div>
          <h1 className="text-2xl sm:text-3xl font-bold tracking-tight text-slate-900" data-testid="admin-volunteers-title">Annuaire des bénévoles</h1>
          <p className="text-sm text-slate-500 mt-1">Visible uniquement par les membres du bureau (RGPD).</p>
        </div>
        <Button className="bg-sky-700 hover:bg-sky-800" onClick={openCreate} data-testid="volunteer-create-btn">
          <Plus className="h-4 w-4 mr-2" />Ajouter un bénévole
        </Button>
      </div>

      <Card className="border-slate-200 overflow-hidden">
        <div className="overflow-x-auto">
          <Table data-testid="volunteers-table">
            <TableHeader>
              <TableRow>
                <TableHead>Nom</TableHead>
                <TableHead>Mission</TableHead>
                <TableHead>Contact</TableHead>
                <TableHead>Consentement</TableHead>
                <TableHead>Statut</TableHead>
                <TableHead className="text-right">Actions</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {volunteers.length === 0 && (
                <TableRow><TableCell colSpan={6} className="text-center text-sm text-slate-500 py-8" data-testid="volunteers-empty">
                  Aucun bénévole enregistré.
                </TableCell></TableRow>
              )}
              {volunteers.map((v) => (
                <TableRow key={v.id} data-testid={`volunteer-row-${v.id}`}>
                  <TableCell className="font-medium">{v.first_name} {v.last_name}</TableCell>
                  <TableCell className="text-sm">{v.mission || "—"}</TableCell>
                  <TableCell className="text-sm">
                    {v.email && <span className="block">{v.email}</span>}
                    {v.phone && <span className="block text-slate-500">{v.phone}</span>}
                    {!v.email && !v.phone && "—"}
                  </TableCell>
                  <TableCell>
                    <Badge className={v.consent_rgpd
                      ? "bg-emerald-100 text-emerald-800 border border-emerald-200 hover:bg-emerald-100"
                      : "bg-red-100 text-red-800 border border-red-200 hover:bg-red-100"}>
                      {v.consent_rgpd ? "Donné" : "Manquant"}
                    </Badge>
                  </TableCell>
                  <TableCell>
                    <Badge className={v.active
                      ? "bg-sky-100 text-sky-800 border border-sky-200 hover:bg-sky-100"
                      : "bg-slate-100 text-slate-600 border border-slate-200 hover:bg-slate-100"}>
                      {v.active ? "Actif" : "Inactif"}
                    </Badge>
                  </TableCell>
                  <TableCell className="text-right space-x-2">
                    <Button variant="outline" size="sm" onClick={() => openEdit(v)} data-testid={`volunteer-edit-${v.id}`}>
                      <Pencil className="h-3.5 w-3.5" />
                    </Button>
                    <Button variant="outline" size="sm" onClick={() => remove(v)} data-testid={`volunteer-delete-${v.id}`}>
                      <Trash2 className="h-3.5 w-3.5 text-red-600" />
                    </Button>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>
      </Card>

      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="max-w-lg" data-testid="volunteer-dialog">
          <DialogHeader>
            <DialogTitle className="font-heading">{editing ? "Modifier le bénévole" : "Nouveau bénévole"}</DialogTitle>
            <DialogDescription>Fiche bénévole de l'annuaire interne, visible uniquement par le bureau.</DialogDescription>
          </DialogHeader>
          <form onSubmit={handleSubmit} className="space-y-4">
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-2">
                <Label>Prénom</Label>
                <Input required value={form.first_name} onChange={(e) => setForm({ ...form, first_name: e.target.value })} data-testid="volunteer-form-firstname" />
              </div>
              <div className="space-y-2">
                <Label>Nom</Label>
                <Input required value={form.last_name} onChange={(e) => setForm({ ...form, last_name: e.target.value })} data-testid="volunteer-form-lastname" />
              </div>
            </div>
            <div className="space-y-2">
              <Label>Mission</Label>
              <Input value={form.mission} onChange={(e) => setForm({ ...form, mission: e.target.value })} placeholder="Ex : logistique kermesse" data-testid="volunteer-form-mission" />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-2">
                <Label>Email</Label>
                <Input type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} data-testid="volunteer-form-email" />
              </div>
              <div className="space-y-2">
                <Label>Téléphone</Label>
                <Input value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} data-testid="volunteer-form-phone" />
              </div>
            </div>
            <div className="flex items-start gap-3 rounded-lg bg-slate-50 p-3">
              <Checkbox id="volunteer-consent" checked={form.consent_rgpd}
                onCheckedChange={(v) => setForm({ ...form, consent_rgpd: !!v })} data-testid="volunteer-form-consent" />
              <Label htmlFor="volunteer-consent" className="font-normal text-xs leading-relaxed text-slate-600 cursor-pointer">
                Le bénévole a donné son consentement explicite pour figurer dans l'annuaire interne (RGPD).
              </Label>
            </div>
            <div className="flex items-center gap-3">
              <Checkbox id="volunteer-active" checked={form.active}
                onCheckedChange={(v) => setForm({ ...form, active: !!v })} data-testid="volunteer-form-active" />
              <Label htmlFor="volunteer-active" className="font-normal text-sm">Bénévole actif</Label>
            </div>
            <Button type="submit" className="w-full bg-sky-700 hover:bg-sky-800" disabled={saving} data-testid="volunteer-form-submit">
              {saving ? "Enregistrement…" : editing ? "Enregistrer" : "Ajouter"}
            </Button>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
}
